import { createFileRoute } from "@tanstack/react-router";
import React from "react";
import {
  Activity,
  Bell,
  Building2,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Database,
  Download,
  FileText,
  Settings,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  Users,
  Workflow,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useAdminDashboard } from "@/hooks/useAdminDashboard";
import { useAdminPartners, useAdminStudents } from "@/hooks/useAdminData";
import { useAuthStore } from "@/store/authStore";
import { PageSkeleton } from "@/components/shared/Skeletons";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { chartAxisTick, chartTooltipProps, PeopleAvatar } from "@/components/admin/people-ui";

export const Route = createFileRoute("/admin/dashboard/")({ component: AdminDashboard });

const BAR_COLORS = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)"];

function AdminDashboard() {
  const { user } = useAuthStore();
  const { data: stats, isLoading } = useAdminDashboard();
  const { data: partnersList } = useAdminPartners();
  const { data: studentsList } = useAdminStudents();

  if (isLoading) {
    return <PageSkeleton />;
  }

  const growth = stats?.trends || [];
  const dist = stats?.distribution || [];
  const recentActivities = stats?.recent_activities || [];
  const displayPartners = stats?.top_partners || [];
  const totalPartners = (partnersList ?? []).length;
  const totalStudents = (studentsList ?? []).length;
  const activeUsers = totalStudents + totalPartners;
  const growthRate = stats?.growth ?? 0;

  const healthItems: { label: string; value: number; icon: React.ReactNode }[] = [
    { label: "Database integrity", value: 100, icon: <Database /> },
    { label: "Access controls", value: 100, icon: <ShieldCheck /> },
    {
      label: "Activity stream",
      value: recentActivities.length > 0 ? 92 : 100,
      icon: <Activity />,
    },
  ];

  const quickActions = [
    {
      title: "Manage students",
      subtitle: "Review profiles and status",
      icon: <Users />,
      to: "/admin/students",
    },
    {
      title: "Manage partners",
      subtitle: "Company allocation and health",
      icon: <Building2 />,
      to: "/admin/partners",
    },
    {
      title: "Review reports",
      subtitle: "Exports and analytics",
      icon: <FileText />,
      to: "/admin/reports",
    },
  ];

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        eyebrow="Admin"
        title="Admin command center"
        subtitle={`Welcome back, ${user?.name?.split(" ")[0] || "Admin"}. Monitor platform health, placements, and partner performance.`}
        action={
          <>
            <Button variant="outline" asChild>
              <Link to="/admin/settings">
                <Settings />
                Platform settings
              </Link>
            </Button>
            {/* `!` beats the global unlayered `a { color: inherit }` rule. */}
            <Button asChild>
              <Link to="/admin/reports">
                <Download />
                Export report
              </Link>
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 2xl:grid-cols-4">
            <StatCard
              label="Total students"
              value={totalStudents}
              icon={<Users />}
              trend={{
                value: `${growthRate >= 0 ? "+" : ""}${growthRate}% growth`,
                positive: growthRate >= 0,
              }}
              hint=""
            />
            <StatCard
              label="Active partners"
              value={totalPartners}
              icon={<Building2 />}
              hint="Partner network"
            />
            <StatCard
              label="Active placements"
              value={stats?.total_placements ?? 0}
              icon={<Workflow />}
              hint="Live assignments"
            />
            <StatCard
              label="Hours logged"
              value={stats?.total_hours ?? 0}
              icon={<ClipboardList />}
              hint="Verified workload"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]">
            <SectionCard
              title="Registration growth"
              description="Student and partner onboarding trend"
              actions={
                <Badge variant={growthRate >= 0 ? "success" : "destructive"}>
                  {growthRate >= 0 ? (
                    <TrendingUp className="size-3" aria-hidden />
                  ) : (
                    <TrendingDown className="size-3" aria-hidden />
                  )}
                  {growthRate >= 0 ? "+" : ""}
                  {growthRate}%
                </Badge>
              }
            >
              <div className="mb-3 flex items-center gap-4 text-caption text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-2 rounded-full bg-chart-1" aria-hidden />
                  Students
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-2 rounded-full bg-chart-3" aria-hidden />
                  Partners
                </span>
              </div>
              <div className="h-64 min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={growth} margin={{ left: -8, right: 8, top: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={chartAxisTick}
                      dy={8}
                    />
                    <YAxis axisLine={false} tickLine={false} tick={chartAxisTick} width={42} />
                    <RechartsTooltip
                      {...chartTooltipProps}
                      cursor={{ stroke: "var(--border)" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="students"
                      name="Students"
                      stroke="var(--chart-1)"
                      strokeWidth={1.75}
                      fill="var(--chart-1)"
                      fillOpacity={0.05}
                    />
                    <Area
                      type="monotone"
                      dataKey="partners"
                      name="Partners"
                      stroke="var(--chart-3)"
                      strokeWidth={1.5}
                      fill="transparent"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>

            <SectionCard title="System health" description="Core service readiness">
              <ul className="space-y-5">
                {healthItems.map((item) => (
                  <li key={item.label}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2 text-sm text-foreground [&_svg]:size-4 [&_svg]:text-muted-foreground">
                        {item.icon}
                        {item.label}
                      </span>
                      <span className="font-mono text-caption text-success tabular-nums">
                        {item.value}%
                      </span>
                    </div>
                    <Progress value={item.value} aria-label={`${item.label} ${item.value}%`} />
                  </li>
                ))}
              </ul>
            </SectionCard>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(300px,0.9fr)]">
            <SectionCard
              title="Partner industries"
              description="Sector distribution of registered partners"
            >
              <div className="h-64 min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dist} layout="vertical" margin={{ left: 0, right: 18 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={false}
                      stroke="var(--border)"
                    />
                    <XAxis type="number" hide />
                    <YAxis
                      dataKey="name"
                      type="category"
                      axisLine={false}
                      tickLine={false}
                      tick={chartAxisTick}
                      width={72}
                    />
                    <RechartsTooltip {...chartTooltipProps} />
                    <Bar dataKey="count" radius={[0, 8, 8, 0]} barSize={18}>
                      {dist.map((_: any, index: number) => (
                        <Cell key={index} fill={BAR_COLORS[index % 4]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>

            <SectionCard
              title="Top performing partners"
              description="Active student allocation"
              noPadding
              actions={
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/admin/partners">View all</Link>
                </Button>
              }
            >
              {displayPartners.length > 0 ? (
                <ul className="max-h-80 divide-y divide-border overflow-auto">
                  {displayPartners.slice(0, 5).map((partner: any) => (
                    <li
                      key={partner.id || partner.name}
                      className="flex min-h-11 items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted/60 md:px-5"
                    >
                      <PeopleAvatar name={partner.name || "P"} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">
                          {partner.name || "Partner"}
                        </p>
                        <p className="truncate text-caption text-muted-foreground">
                          {partner.industry || "Industry"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm text-foreground tabular-nums">
                          {partner.students ?? 0}
                        </p>
                        <p className="eyebrow text-[10px]">Active</p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState
                  bare
                  icon={<Building2 />}
                  title="No partner performance data yet"
                  description="Partners appear here once students are allocated to them."
                />
              )}
            </SectionCard>
          </div>
        </div>

        <aside className="flex min-w-0 flex-col gap-4">
          <SectionCard title="Operation pulse">
            <div className="space-y-3">
              <div className="rounded-sm border border-border bg-muted p-3">
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground">Platform status</span>
                  <Badge variant="success" dot>
                    Healthy
                  </Badge>
                </div>
                <p className="text-caption leading-5 text-muted-foreground">
                  Core dashboard services are responding with current operational data.
                </p>
              </div>
              <dl className="grid grid-cols-2 gap-3">
                <div className="rounded-sm border border-border p-3">
                  <dt className="eyebrow">Active users</dt>
                  <dd className="mt-1 text-xl text-foreground tabular-nums">
                    {activeUsers.toLocaleString()}
                  </dd>
                </div>
                <div className="rounded-sm border border-border p-3">
                  <dt className="eyebrow">Uptime</dt>
                  <dd className="mt-1 text-xl text-foreground tabular-nums">100%</dd>
                </div>
              </dl>
            </div>
          </SectionCard>

          <SectionCard
            title="Recent activity"
            noPadding
            actions={
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-muted-foreground hover:text-foreground"
                asChild
              >
                <Link to="/admin/audit-logs" aria-label="Open audit logs" title="Open audit logs">
                  <FileText />
                </Link>
              </Button>
            }
          >
            {recentActivities.length > 0 ? (
              <ul className="divide-y divide-border">
                {recentActivities
                  .slice(0, 6)
                  .map((activity: { text: string; time: string; type: string }, index: number) => (
                    <li
                      key={`${activity.text}-${index}`}
                      className="flex gap-3 px-4 py-3 transition-colors hover:bg-muted/60 md:px-5"
                    >
                      <span
                        className="flex size-7 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-muted-foreground [&_svg]:size-3.5"
                        aria-hidden
                      >
                        {activity.type === "registration" ? <Users /> : <Bell />}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm leading-5 text-foreground">{activity.text}</p>
                        <p className="mt-0.5 text-caption text-muted-foreground">
                          {activity.time}
                        </p>
                      </div>
                    </li>
                  ))}
              </ul>
            ) : (
              <EmptyState
                bare
                icon={<CheckCircle2 />}
                title="No recent platform activity"
                description="New registrations and events will show up here."
              />
            )}
          </SectionCard>

          <SectionCard title="Quick actions" noPadding>
            <ul className="divide-y divide-border">
              {quickActions.map((action) => (
                <li key={action.title}>
                  <Link
                    to={action.to}
                    className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none md:px-5"
                  >
                    <span
                      className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-body [&_svg]:size-4"
                      aria-hidden
                    >
                      {action.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground">
                        {action.title}
                      </span>
                      <span className="block text-caption text-muted-foreground">
                        {action.subtitle}
                      </span>
                    </span>
                    <ChevronRight
                      className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                      aria-hidden
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </SectionCard>
        </aside>
      </div>
    </div>
  );
}
